import { Check } from "lucide-react";

/**
 * RadioCardGroup — accessible radio cards built on native <input type="radio">.
 *
 * Props:
 *   name           – form field name
 *   value          – selected string
 *   onChange(v)    – called with selected string
 *   options        – Array<string | { value, label, description?, icon? }>
 *   columns        – 1 | 2 | 3 | 4 (default 2)
 *   testIdPrefix   – data-testid prefix for each card (default = name)
 *   error          – error message string (shows below the grid)
 *   ariaLabel      – aria-label for the radiogroup
 */
export default function RadioCardGroup({
  name,
  value,
  onChange,
  options,
  columns = 2,
  testIdPrefix,
  error,
  ariaLabel,
}) {
  const items = options.map((o) =>
    typeof o === "string" ? { value: o, label: o } : o
  );
  const prefix = testIdPrefix || name;
  const colCls =
    columns === 1
      ? "grid-cols-1"
      : columns === 3
      ? "grid-cols-1 sm:grid-cols-3"
      : columns === 4
      ? "grid-cols-2 sm:grid-cols-4"
      : "grid-cols-1 sm:grid-cols-2";

  return (
    <div>
      <div
        role="radiogroup"
        aria-label={ariaLabel || name}
        aria-invalid={!!error}
        className={`grid gap-3 ${colCls}`}
        data-testid={`${prefix}-group`}
      >
        {items.map((opt) => {
          const isSelected = value === opt.value;
          const Icon = opt.icon;
          const slug = String(opt.value)
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/(^-|-$)/g, "");
          const id = `${prefix}-${slug}`;
          return (
            <label
              key={opt.value}
              htmlFor={id}
              data-testid={`${prefix}-card-${slug}`}
              className={`group relative flex cursor-pointer items-start gap-3 rounded-2xl border bg-white p-4 shadow-soft transition-all hover:-translate-y-0.5 ${
                isSelected
                  ? "border-aqua-deep ring-2 ring-aqua/30 bg-aqua/[0.04]"
                  : "border-mist-line hover:border-aqua/45"
              }`}
            >
              <input
                id={id}
                type="radio"
                name={name}
                value={opt.value}
                checked={isSelected}
                onChange={() => onChange(opt.value)}
                className="sr-only"
              />
              {Icon && (
                <span
                  className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl transition-colors ${
                    isSelected
                      ? "bg-navy text-aqua"
                      : "bg-mist text-navy/65 group-hover:bg-navy group-hover:text-aqua"
                  }`}
                  aria-hidden="true"
                >
                  <Icon className="h-4 w-4" />
                </span>
              )}
              <div className="min-w-0 flex-1">
                <p className="font-display text-[15px] font-bold leading-tight text-navy">
                  {opt.label}
                </p>
                {opt.description && (
                  <p className="mt-1 text-xs leading-snug text-navy/65">
                    {opt.description}
                  </p>
                )}
              </div>
              <span
                aria-hidden="true"
                className={`inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border transition-all ${
                  isSelected
                    ? "border-aqua-deep bg-aqua-deep text-white"
                    : "border-mist-line bg-white text-transparent"
                }`}
              >
                <Check className="h-3 w-3" />
              </span>
            </label>
          );
        })}
      </div>
      {error && (
        <p
          role="alert"
          data-testid={`${prefix}-error`}
          className="mt-2 text-xs font-medium text-red-600"
        >
          {error}
        </p>
      )}
    </div>
  );
}
